<template>
  <div class="tw-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <header class="tw-topbar">
      <button type="button" class="tw-topbar__back" aria-label="Back to products" @click="goBack">
        <v-icon icon="mdi-arrow-left" size="18" />
      </button>
      <div class="tw-topbar__copy">
        <h1 class="tw-topbar__title">{{ context?.context_name || 'Product workspace' }}</h1>
      </div>

      <div class="tw-topbar__tabs" role="tablist" aria-label="Product sections">
        <button
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'dashboard' }"
          role="tab"
          :aria-selected="pageTab === 'dashboard'"
          @click="pageTab = 'dashboard'"
        >
          Dashboard
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'fields' }"
          role="tab"
          :aria-selected="pageTab === 'fields'"
          @click="pageTab = 'fields'"
        >
          Form fields
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'dispositions' }"
          role="tab"
          :aria-selected="pageTab === 'dispositions'"
          @click="pageTab = 'dispositions'"
        >
          Status
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'reports' }"
          role="tab"
          :aria-selected="pageTab === 'reports'"
          @click="pageTab = 'reports'"
        >
          Reports
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'reportColumns' }"
          role="tab"
          :aria-selected="pageTab === 'reportColumns'"
          @click="pageTab = 'reportColumns'"
        >
          Report columns
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'settings' }"
          role="tab"
          :aria-selected="pageTab === 'settings'"
          @click="pageTab = 'settings'"
        >
          Settings
        </button>
      </div>
    </header>

    <div v-if="!loading && !context" class="tw-empty" style="margin: 3rem auto">
      <v-icon icon="mdi-alert-circle-outline" size="36" />
      <p class="tw-empty__title">Product not found</p>
      <p class="tw-empty__sub">It may have been removed, or you don't have access to it.</p>
    </div>

    <div v-show="pageTab === 'dashboard' && context" class="tw-dashboard-pane tw-dashboard-pane--page">
      <CampaignDashboardPanel
        :campaign-id="contextId"
        :campaign-name="context?.context_name || ''"
        scope-type="PRODUCT"
        :converted-status="metricsConvertedStatus"
        :active="pageTab === 'dashboard'"
        :is-admin="isAdmin"
        show-header
        :show-title="false"
      />
    </div>

    <div v-show="pageTab === 'fields' && context" class="tw-reports-pane">
      <header class="tw-reports-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Product form fields</p>
          <h2 class="tw-reports-pane__title">
            {{ context?.context_name || 'Form fields' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Custom fields leads for this product carry, beyond the built-in name/phone/email.
          </p>
        </div>
      </header>
      <div class="tw-settings-pane__body">
        <ContextFormFieldsTab
          :context="context"
          @saved="onSubTabSaved"
          @error="(msg) => showSnack('error', msg)"
        />
      </div>
    </div>

    <div v-show="pageTab === 'dispositions' && context" class="tw-reports-pane">
      <header class="tw-reports-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Product status</p>
          <h2 class="tw-reports-pane__title">
            {{ context?.context_name || 'Status' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Lead statuses agents can set for leads under this product.
          </p>
        </div>
      </header>
      <div class="tw-settings-pane__body">
        <ContextDispositionsTab
          :context="context"
          @saved="onSubTabSaved"
          @error="(msg) => showSnack('error', msg)"
        />
      </div>
    </div>

    <div v-show="pageTab === 'reports' && context" class="tw-reports-pane">
      <header class="tw-reports-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Product reports</p>
          <h2 class="tw-reports-pane__title">
            {{ context?.context_name || 'Reports' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Export-ready summaries across every campaign under this product.
          </p>
        </div>
      </header>
      <CampaignReportsPanel
        :campaign-id="contextId"
        :campaign-name="context?.context_name || ''"
        scope-type="PRODUCT"
        :converted-status="metricsConvertedStatus"
      />
    </div>

    <div v-show="pageTab === 'reportColumns' && context" class="tw-reports-pane">
      <header class="tw-reports-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Product report columns</p>
          <h2 class="tw-reports-pane__title">
            {{ context?.context_name || 'Report columns' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Which columns each report exports, and in what order, for this product.
          </p>
        </div>
      </header>
      <div class="tw-settings-pane__body">
        <ContextReportColumnsTab
          :context="context"
          @saved="onSubTabSaved"
          @error="(msg) => showSnack('error', msg)"
        />
      </div>
    </div>

    <div v-show="pageTab === 'settings' && context" class="tw-reports-pane">
      <header class="tw-reports-pane__head tw-settings-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Product settings</p>
          <h2 class="tw-reports-pane__title">
            {{ context?.context_name || 'Settings' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Lead creation for campaigns under this product, and periodic KPI/agent-activity
            digests across all its campaigns. Greeting and conversation instructions are set
            per-campaign now, not here.
          </p>
        </div>
        <button
          type="button"
          class="tw-btn tw-btn--primary"
          :disabled="savingSettings"
          @click="saveSettings"
        >
          <v-icon icon="mdi-content-save-outline" size="16" />
          {{ savingSettings ? 'Saving…' : 'Save' }}
        </button>
      </header>
      <div class="tw-settings-pane__body">
        <section class="ctx-settings__card">
          <div class="ctx-settings__card-head">
            <h3 class="ctx-settings__card-title">Lead creation</h3>
            <p class="ctx-settings__card-copy">
              Whether campaigns using this product can create leads manually.
            </p>
          </div>
          <label class="ctx-settings__field">
            <v-switch
              v-model="settingsForm.allowManualLeads"
              label="Allow creating leads from campaigns"
              color="primary"
              density="compact"
              hide-details
              inset
              :disabled="savingSettings"
            />
            <span class="ctx-settings__hint">
              Shows a "Create lead" button on campaign pages using this product.
            </span>
          </label>
        </section>

        <section class="ctx-settings__card">
          <div class="ctx-settings__card-head">
            <h3 class="ctx-settings__card-title">Dialling</h3>
            <p class="ctx-settings__card-copy">
              Default limit on how many times a lead under this product can be dialed in one day,
              across every campaign using it. A campaign can set its own override in its own
              Settings tab; leave this blank to use the platform default.
            </p>
          </div>
          <label class="ctx-settings__field">
            <input
              v-model="settingsForm.dailyDialCap"
              type="number"
              min="1"
              step="1"
              class="tw-input"
              placeholder="Platform default"
              :disabled="savingSettings"
              style="max-width: 160px"
            />
            <span class="ctx-settings__hint">Max dials per lead per day, for campaigns under this product.</span>
          </label>
        </section>

        <section class="ctx-settings__card">
          <div class="ctx-settings__card-head">
            <h3 class="ctx-settings__card-title">Dashboard Digest</h3>
            <p class="ctx-settings__card-copy">
              Periodically email/WhatsApp this product's KPIs and agent activity across all its
              campaigns, instead of relying on someone to come check the dashboard.
            </p>
          </div>
          <DashboardDigestSettings
            v-model="digestSettings"
            v-model:send-now="digestSendNow"
            scope-label="product"
            :disabled="savingSettings"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import CampaignDashboardPanel from '@/components/Campaigns/CampaignDashboardPanel.vue'
import CampaignReportsPanel from '@/components/Campaigns/CampaignReportsPanel.vue'
import DashboardDigestSettings from '@/components/Shared/DashboardDigestSettings.vue'
import ContextFormFieldsTab from '@/components/Contexts/Tabs/ContextFormFieldsTab.vue'
import ContextDispositionsTab from '@/components/Contexts/Tabs/ContextDispositionsTab.vue'
import ContextReportColumnsTab from '@/components/Contexts/Tabs/ContextReportColumnsTab.vue'
import { triggerDashboardDigestNow } from '@/api/dashboardDigestApi'
import { fetchContexts, updateContext } from '@/api/contextsApi'
import { normalizeContextsList } from '@/utils/contexts'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import '@/styles/campaigns/campaign-workspace.css'

defineOptions({ name: 'ContextDetailPage' })

const store = useStore()
const route = useRoute()
const router = useRouter()

const snackbar = ref({})
const context = ref(null)
const loading = ref(false)
const pageTab = ref('dashboard')
const savingSettings = ref(false)
const digestSendNow = ref(false)
// Greeting/mission_prompt live at the campaign level now (CreateCampaignDialog.vue) --
// not duplicated here.
const settingsForm = ref({
  allowManualLeads: true,
  dailyDialCap: '',
})
const digestSettings = ref({
  send_dashboard_digest_by_email: false,
  dashboard_digest_email_addresses: [],
  send_dashboard_digest_by_whatsapp: false,
  dashboard_digest_whatsapp_numbers: [],
  dashboard_digest_cc_agents: false,
  dashboard_digest_times: [],
  dashboard_digest_weekly_enabled: false,
  dashboard_digest_weekly_day: '',
  dashboard_digest_weekly_time: '',
  dashboard_digest_monthly_enabled: false,
  dashboard_digest_monthly_day: null,
  dashboard_digest_monthly_time: '',
})

const contextId = computed(() => String(route.params.contextId ?? '').trim())

const isAdmin = computed(() => {
  const role = String(store.state.userRole ?? '').trim().toUpperCase()
  return role === 'ADMIN' || role === 'OWNER'
})

// Same "find the product's own configured Converted-equivalent status"
// logic CampaignDetailPage.vue uses per-campaign, just off this ONE
// product's own lead_statuses directly (a product has exactly one set,
// unlike a campaign which can span several products).
const metricsConvertedStatus = computed(() => {
  const statuses = Array.isArray(context.value?.lead_statuses) ? context.value.lead_statuses : []
  const match = statuses.find((status) => {
    const value = String(
      typeof status === 'string' ? status : status?.value ?? status?.status ?? '',
    ).trim().toUpperCase()
    return value === 'CONVERTED' || value.includes('CONVERT')
  })
  if (match) {
    return String(typeof match === 'string' ? match : match?.value ?? match?.status ?? 'CONVERTED')
      .trim()
      .toUpperCase()
  }
  return 'CONVERTED'
})

function goBack() {
  router.push({ name: 'Contexts' })
}

function showSnack(type, message) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: type === 'error' ? 'error' : 'success',
    SnackbarText: message,
  }
}

// Form fields / Dispositions tabs each keep their own local optimistic
// override for instant feedback -- this just surfaces the message and
// refreshes context.value in the background (e.g. metricsConvertedStatus
// reads lead_statuses off it) without disturbing either tab's own state.
function onSubTabSaved(message) {
  showSnack('success', message)
  void loadContext()
}

function syncSettingsFromContext() {
  const c = context.value
  settingsForm.value = {
    allowManualLeads: c?.allow_manual_leads !== false,
    dailyDialCap: c?.daily_dial_cap != null ? String(c.daily_dial_cap) : '',
  }
  digestSettings.value = {
    send_dashboard_digest_by_email: c?.send_dashboard_digest_by_email === true,
    dashboard_digest_email_addresses: Array.isArray(c?.dashboard_digest_email_addresses)
      ? [...c.dashboard_digest_email_addresses]
      : [],
    send_dashboard_digest_by_whatsapp: c?.send_dashboard_digest_by_whatsapp === true,
    dashboard_digest_whatsapp_numbers: Array.isArray(c?.dashboard_digest_whatsapp_numbers)
      ? [...c.dashboard_digest_whatsapp_numbers]
      : [],
    dashboard_digest_cc_agents: c?.dashboard_digest_cc_agents === true,
    dashboard_digest_times: Array.isArray(c?.dashboard_digest_times)
      ? [...c.dashboard_digest_times]
      : [],
    dashboard_digest_weekly_enabled: c?.dashboard_digest_weekly_enabled === true,
    dashboard_digest_weekly_day: c?.dashboard_digest_weekly_day || '',
    dashboard_digest_weekly_time: c?.dashboard_digest_weekly_time || '',
    dashboard_digest_monthly_enabled: c?.dashboard_digest_monthly_enabled === true,
    dashboard_digest_monthly_day: c?.dashboard_digest_monthly_day ?? null,
    dashboard_digest_monthly_time: c?.dashboard_digest_monthly_time || '',
  }
}

async function saveSettings() {
  if (!contextId.value || !context.value) return
  savingSettings.value = true
  try {
    const response = await updateContext({
      context_id: contextId.value,
      context_name: context.value.context_name,
      allow_manual_leads: settingsForm.value.allowManualLeads !== false,
      daily_dial_cap: settingsForm.value.dailyDialCap ? Number(settingsForm.value.dailyDialCap) : null,
      ...digestSettings.value,
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Could not save product settings.'))
      return
    }
    await loadContext()

    if (digestSendNow.value) {
      const sendResponse = await triggerDashboardDigestNow({
        scope_type: 'PRODUCT',
        scope_id: contextId.value,
      })
      digestSendNow.value = false
      if (!isApiSuccess(sendResponse)) {
        showSnack('error', getApiMessage(sendResponse, 'Settings saved, but the immediate send failed.'))
        return
      }
      showSnack('success', 'Settings saved and Dashboard Digest sent.')
      return
    }

    showSnack('success', 'Settings saved.')
  } catch (err) {
    showSnack('error', getApiMessage(err, 'Could not save product settings.'))
  } finally {
    savingSettings.value = false
  }
}

async function loadContext() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId || !contextId.value) return
  loading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    if (!isApiSuccess(response)) {
      context.value = null
      return
    }
    const list = normalizeContextsList(unwrapApiList(response))
    context.value = list.find((item) => String(item.context_id) === contextId.value) || null
    syncSettingsFromContext()
  } catch {
    context.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void loadContext()
})
</script>

<style scoped>
.tw-page {
  min-height: 100%;
}

.ctx-settings__card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--tw-card);
  border: 1px solid var(--tw-line);
  border-radius: var(--tw-radius);
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
}
.ctx-settings__card:last-child {
  margin-bottom: 0;
}
.ctx-settings__card-head {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.ctx-settings__card-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--tw-text);
}
.ctx-settings__card-copy {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--tw-muted);
  max-width: 40rem;
}
.ctx-settings__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.ctx-settings__label {
  font-size: 0.82rem;
  font-weight: 650;
  color: var(--tw-text);
}
.ctx-settings__hint {
  font-size: 0.75rem;
  color: var(--tw-muted);
}
</style>
